import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getClient } from '@chegg/rio-sdk-web';
import prepareEventData from './utils/prepareEventData';
import useTrackingData from './utils/useTrackingData';
import useIsMounted from './utils/useIsMounted';
import useOnVisibilityChange from './utils/useOnVisibilityChange';
// Intervals in which the component will wait to send each log
// The idea is to send a log at 15 seconds, 30 seconds, 60 seconds, etc.
export var delayIntervals = [5, 15, 30, 60, 120, 240];

/**
 * Heartbeat time on page tracking is designed to send multiple RIO events over the
 * span of time the user stays on a page, in a heartbeat pattern
 * @deprecated Please use this hook if you need heartbeat data, in other cases please use useOnUnloadTimeOnPage
 */
var useHeartbeat = function useHeartbeat(params) {
  var client = getClient();
  var isMounted = useIsMounted();
  // Intervals in which to log the user is present on the page
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    heartbeats = _useState2[0],
    setHeartbeats = _useState2[1];
  // Flag that indicated that heartbeat is active
  var heartbeatActive = useMemo(function () {
    return heartbeats < delayIntervals.length;
  }, [heartbeats]);
  // Subtracting the previous delay to the current, will give the
  // amount of time the user has stayed on the page.
  var _useState3 = useState(delayIntervals[0]),
    _useState4 = _slicedToArray(_useState3, 2),
    delay = _useState4[0],
    setDelay = _useState4[1];
  // Updating delay each time heartbeat is changing
  useEffect(function () {
    var _delayIntervals;
    setDelay(delayIntervals[heartbeats] - ((_delayIntervals = delayIntervals[heartbeats - 1]) !== null && _delayIntervals !== void 0 ? _delayIntervals : 0));
  }, [heartbeats]);
  //Identifying tracking type from url parameters and merging with other parameters
  var trackingData = useTrackingData(params);
  var timer = useRef();
  var timerStart = useRef(Date.now());
  var timerLeft = useRef(0);
  var getTimeLeft = useCallback(function () {
    var elapsed = Math.ceil((Date.now() - timerStart.current) / 1000);
    return delay - elapsed;
  }, [delay]);
  function clearTimer() {
    /* istanbul ignore else */
    if (timer.current) {
      clearTimeout(timer.current);
    }
  }
  var onVisible = useCallback(function () {
    /* istanbul ignore else */
    if (heartbeatActive) {
      setDelay(timerLeft.current);
    }
  }, [heartbeatActive]);
  var onHidden = useCallback(function () {
    /* istanbul ignore else */
    if (heartbeatActive) {
      timerLeft.current = getTimeLeft();
      clearTimer();
    }
  }, [heartbeatActive, getTimeLeft]);
  useOnVisibilityChange({
    onVisible: onVisible,
    onHidden: onHidden
  });

  // Resets heartbeat on receiving updated event data
  useEffect(function () {
    /* istanbul ignore else */
    if (isMounted) {
      setHeartbeats(0);
    }
  }, [trackingData]);

  // Setting timeout for next event
  useEffect(function () {
    if (trackingData && heartbeatActive) {
      clearTimer();
      timerStart.current = Date.now();
      timer.current = setTimeout(function () {
        client.sendEvent(prepareEventData(_objectSpread(_objectSpread({}, trackingData), {}, {
          interval_type: 'heartbeat',
          interval: delayIntervals[heartbeats]
        })));
        setHeartbeats(heartbeats + 1);
      }, delay * 1000);
      return clearTimer;
    }
  }, [heartbeatActive, trackingData, delay, heartbeats]);
};
export default useHeartbeat;